// Shared style builders + controls for the page-presentation editors (Search and Home).
// Extracted so both editors emit identical inline styles and share one control surface.
// The builders mirror the storefront's consumer-side builders (ac-storefront-web:
// Components/Helpers/searchPageConfig.ts) so the admin preview matches the live page.

import React from 'react';
import { ActionButton, Dropdown, Icon, IconButton, IContextualMenuItemProps, IContextualMenuItemRenderFunctions, IDropdownOption, Stack, TextField, TooltipHost, useTheme } from '@fluentui/react';
import StorefrontColorPickerButton, { IStyleEntry } from './StorefrontColorPickerButton';
import {
    ISearchCardStyle,
    ISearchContainerStyle,
    ISearchElementStyle,
    ISearchPriceStyle,
    ISearchTextStyle,
} from './searchPageConfig';
import { IStorefrontStyle } from '../../../models/Storefront';
import {
    countLocalOverrides,
    findStyle,
    getStyleRef,
    isGlobalStyle,
    IStyleTarget,
    resolveActiveStyle,
    resolveCardStyleGroup,
    STYLE_REF_KEY,
    stylesForTarget,
} from './stylesConfig';
import { useStorefrontStyles } from './StorefrontStylesContext';
import { ColorRow, RowGroupLabel, StyleRow, TextAlignRow, useCellControlStyles } from './styleRows';
import FontPicker from '../../Inputs/FontPicker';
import { useSettingReveal } from '../../../hooks/useSettingReveal';
import { DynamicSetting } from '../../Common/Setting';

export const BORDER_STYLE_OPTIONS: IDropdownOption[] = [
    { key: '', text: 'Default' },
    { key: 'solid', text: 'Solid' },
    { key: 'dashed', text: 'Dashed' },
    { key: 'dotted', text: 'Dotted' },
    { key: 'none', text: 'None' },
];

export const TEXT_TRANSFORM_OPTIONS: IDropdownOption[] = [
    { key: '', text: 'Default' },
    { key: 'none', text: 'None' },
    { key: 'uppercase', text: 'UPPERCASE' },
    { key: 'lowercase', text: 'lowercase' },
    { key: 'capitalize', text: 'Capitalize' },
];

export const TEXT_ALIGN_OPTIONS: IDropdownOption[] = [
    { key: '', text: 'Default' },
    { key: 'left', text: 'Left' },
    { key: 'center', text: 'Center' },
    { key: 'right', text: 'Right' },
    { key: 'justify', text: 'Justify' },
];

// Letter-spacing is stored as a length string (e.g. "2px"); parse the numeric px back out
// for the control and re-emit "{n}px" (or undefined when cleared).
export const parsePx = (v?: string): number | undefined => {
    if (v === undefined) return undefined;
    const n = parseInt(String(v), 10);
    return isNaN(n) ? undefined : n;
};

export const FONT_WEIGHT_OPTIONS: IDropdownOption[] = [
    { key: '', text: 'Default' },
    { key: 'normal', text: 'Normal' },
    { key: '500', text: 'Medium (500)' },
    { key: '600', text: 'Semibold (600)' },
    { key: 'bold', text: 'Bold' },
];

export const parseNum = (v?: string): number | undefined => {
    if (v === undefined || v.trim() === '') return undefined;
    const n = parseInt(v, 10);
    return isNaN(n) ? undefined : n;
};

// Line height is stored as a UNITLESS STRING ('1.5'), never a number. A numeric value would be
// serialized as a px length by cssText here and by the storefront's importantStyleRef (both append
// 'px' to numbers), turning a 1.5x multiplier into a 1.5px line. A string passes through every
// path — React inline styles, the scoped <style> the Home editor builds, and the storefront —
// unchanged. Empty/invalid input clears the property so the element inherits again.
export const parseLineHeight = (v?: string): string | undefined => {
    if (v === undefined || v.trim() === '') return undefined;
    const n = parseFloat(v);
    return isNaN(n) || n < 0 ? undefined : String(n);
};

export const numFieldStyles = { root: { width: 90 } };

// Build inline style objects from the config so preview elements override the CSS
// defaults only where the user has set a value.
//
// Every builder first resolves the dict's `$styleRef` (Storefront Styles) against the active
// styles list — named preset underneath, local edits on top, `$styleRef` stripped. `part` is the
// part implied by the config key the dict lives on (stylePartForKey in stylesConfig.ts); omitting
// it falls back to the referenced style's default part, so untouched call sites still resolve.
// Dicts without a ref pass through byte-identically, so this is a no-op for every existing config.
// Border longhands, emitted onto `style` ONLY when they actually carry a value.
//
// Emitting `borderStyle: undefined` (and friends) is not the no-op it looks like: React sets the
// DOM property to '', which is a *clear*, and clearing `border-style` also wipes any per-side
// shorthand that arrived in `rest` — `borderBottom: '4px solid #ff9900'` collapses to `none`. That
// is how a configured header divider could vanish from the preview while its box-shadow glow, which
// no longhand touches, still rendered: the header looked "almost right", which is the hardest kind
// of wrong to spot.
//
// The storefront runtime already guards this (ac-storefront-web searchPageConfig.elementStyleFrom);
// the fix never came back across to this copy, so all four builders below share the helper now.
// Loosely typed on purpose: the four style dictionaries this serves declare these fields slightly
// differently (some allow a raw string where another wants a number), and every value is stringified
// or passed straight to React anyway.
interface IBorderParts {
    borderColor?: string;
    borderWidth?: string | number;
    borderStyle?: string | number;
    borderRadius?: string | number;
}

const withBorder = (style: React.CSSProperties, parts: IBorderParts): React.CSSProperties => {
    const { borderColor, borderWidth, borderStyle, borderRadius } = parts;
    // A color or width alone still means "draw a border", and CSS defaults border-style to none —
    // so supply 'solid' in that case, exactly as before.
    const hasBorder = !!borderColor || borderWidth != null || (!!borderStyle && borderStyle !== '');
    if (borderColor) style.borderColor = borderColor;
    if (borderWidth != null) style.borderWidth = borderWidth;
    if (borderStyle) style.borderStyle = String(borderStyle);
    else if (hasBorder) style.borderStyle = 'solid';
    if (borderRadius != null) style.borderRadius = borderRadius;
    return style;
};

export const cardStyleFrom = (raw?: ISearchCardStyle, part = 'card'): React.CSSProperties => {
    const card = resolveActiveStyle(raw, part);
    if (!card) return {};
    const { borderColor, borderWidth, borderStyle, borderRadius, ...rest } = card;
    return withBorder({ ...(rest as React.CSSProperties) }, { borderColor, borderWidth, borderStyle, borderRadius });
};

export const textStyleFrom = (raw?: ISearchTextStyle, part?: string): React.CSSProperties => {
    const t = resolveActiveStyle(raw, part);
    if (!t) return {};
    const { font, fontSize, ...rest } = t;
    return {
        ...(rest as React.CSSProperties),
        fontFamily: font || undefined,
        fontSize: fontSize != null ? `${fontSize}%` : undefined,
    };
};

export const priceStyleFrom = (raw?: ISearchPriceStyle, part = 'price'): React.CSSProperties => {
    // The nested textStyleFrom call below re-enters the resolver, but by then $styleRef is already
    // stripped, so it is a no-op — resolution happens exactly once, here.
    const p = resolveActiveStyle(raw, part);
    if (!p) return {};
    const { borderColor, borderWidth, borderStyle, borderRadius, backgroundColor, padding, ...textRest } = p;
    const hasBorder = !!borderColor || borderWidth != null || (!!borderStyle && borderStyle !== '');
    const hasContainer = !!backgroundColor || hasBorder || !!padding;
    const style: React.CSSProperties = { ...textStyleFrom(textRest) };
    // Same rule as the border longhands: only set what was configured. `backgroundColor: undefined`
    // would clear a `background` shorthand and `padding: undefined` a `paddingBottom`, both of which
    // can legitimately arrive through raw-JSON editing.
    if (backgroundColor) style.backgroundColor = backgroundColor;
    if (padding) style.padding = padding;
    if (hasContainer) style.display = 'inline-block';
    return withBorder(style, { borderColor, borderWidth, borderStyle, borderRadius });
};

export const containerStyleFrom = (raw?: ISearchContainerStyle, part?: string): React.CSSProperties => {
    const c = resolveActiveStyle(raw, part);
    if (!c) return {};
    const { borderColor, borderWidth, borderStyle, borderRadius, ...rest } = c;
    return withBorder({ ...(rest as React.CSSProperties) }, { borderColor, borderWidth, borderStyle, borderRadius });
};

// Generic text + box builder for the paginator/breadcrumb/pod elements (mirrors the
// storefront's elementStyleFrom).
export const elementStyleFrom = (raw?: ISearchElementStyle, part?: string): React.CSSProperties => {
    const s = resolveActiveStyle(raw, part);
    if (!s) return {};
    const { font, fontSize, borderColor, borderWidth, borderStyle, borderRadius, ...rest } = s;
    const style: React.CSSProperties = {
        ...(rest as React.CSSProperties),
    };
    // Only emit the font longhands when they carry a value, matching withBorder's rule and the
    // storefront's own elementStyleFrom. Every hover preview here (product card, category pod, nav
    // pod, the Styles panel) spreads this builder's output OVER the resting style, and an emitted
    // `fontSize: undefined` is a *clear*, not an absence — so a hover that only recolored wiped the
    // configured font and size, shrinking the example on hover. The live renderer emits hover as a
    // scoped CSS rule, which skips undefined, so the preview was shrinking where the store did not.
    if (font) style.fontFamily = font;
    if (fontSize != null) style.fontSize = `${fontSize}%`;
    return withBorder(style, { borderColor, borderWidth, borderStyle, borderRadius });
};

// The inline styles a previewed product card needs, one per visually distinct slot. Built from a
// section's `productStyleProps` (which may carry a `$styleRef` to a card-type Storefront Style):
// the dict itself styles the tile, the referenced style supplies the name/price/image parts.
export interface IProductCardStyles {
    card?: React.CSSProperties;
    title?: React.CSSProperties;
    price?: React.CSSProperties;
    image?: React.CSSProperties;
}

export const productCardStylesFrom = (
    productStyleProps: unknown,
    styles?: IStorefrontStyle[],
): IProductCardStyles | undefined => {
    const group = resolveCardStyleGroup(productStyleProps as Record<string, any> | undefined, styles);
    // The builders re-enter the resolver, but $styleRef is already stripped by now, so they no-op.
    const result: IProductCardStyles = {
        card: group.card ? cardStyleFrom(group.card as ISearchCardStyle, 'card') : undefined,
        title: group.title ? textStyleFrom(group.title as ISearchTextStyle, 'title') : undefined,
        price: group.price ? priceStyleFrom(group.price as ISearchPriceStyle, 'price') : undefined,
        image: group.image ? elementStyleFrom(group.image as ISearchElementStyle, 'image') : undefined,
    };
    return (result.card || result.title || result.price || result.image) ? result : undefined;
};

// The box-shadow/clip-path trick that paints a background color edge-to-edge across the editor canvas
// (see fullBleedBandStyle for why the preview can't use a real 100vw breakout). Returns the properties
// to spread onto an element's style, or undefined when no color is given.
export const fullBleedFromColor = (bg?: string): React.CSSProperties | undefined =>
    bg ? { backgroundColor: bg, boxShadow: `0 0 0 100vmax ${bg}`, clipPath: 'inset(0 -100vmax)' } : undefined;

// Full-bleed "band" style for the Home editor preview. The live storefront breaks a row out to
// 100vw (HomePage.tsx fullBleedBand); the editor preview is a fixed-width canvas (.storefront-body
// inside .storefront-editor), so a 100vw breakout would blow past the panel. Instead we paint the
// band's background across the whole canvas with the box-shadow/clip-path full-bleed trick — the
// element stays in its normal max-width flow, and .storefront-editor's overflow-x: clip stops the
// bleed at the canvas edge. Returns undefined when the config sets no styling.
export const fullBleedBandStyle = (s?: ISearchElementStyle): React.CSSProperties | undefined => {
    if (!s) return undefined;
    const style = elementStyleFrom(s);
    if (!Object.keys(style).length) return undefined;
    const bg = style.backgroundColor as string | undefined;
    // A band painted with a gradient or image and NO solid colour. The box-shadow trick below is
    // useless here — a shadow can spread a colour outward but never a gradient — so such a band used
    // to render at the content-frame width while the shopper saw it bleeding edge to edge. Fall back
    // to the real 100vw breakout the storefront itself uses: .storefront-editor's overflow-x: clip
    // bounds it at the canvas edge, which is the whole reason it is safe here.
    //
    // The gradient then spans the browser viewport rather than the canvas, so the canvas shows its
    // middle slice — a close approximation rather than a pixel match, and far closer than a band that
    // stops mid-canvas. Backgrounds only: the band's own borders/shadow ride along on the same box.
    if (!bg) {
        const hasPaintedBackground = !!style.backgroundImage || !!(style as Record<string, unknown>).background;
        return hasPaintedBackground
            ? { ...style, width: '100vw', maxWidth: 'none', marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)' }
            : style;
    }
    const bleed = `0 0 0 100vmax ${bg}`;
    return {
        ...style,
        ...fullBleedFromColor(bg),
        // fullBleedFromColor's shadow is a PAINT TRICK, not styling, and spreading it wholesale used
        // to drop whatever boxShadow the merchant had configured on the band — so a band with both a
        // colour and a glow silently lost the glow in the preview while keeping it on the storefront.
        boxShadow: style.boxShadow ? `${String(style.boxShadow)}, ${bleed}` : bleed,
    };
};

// Convert a builder's React.CSSProperties into a CSS declaration string with !important
// on every property. Used to build a scoped <style> for the Home editor preview so the
// existing (deeply nested) section/category preview components need no changes at all.
// Numbers become px lengths, matching importantStyleRef.
export const cssText = (style: React.CSSProperties): string =>
    Object.entries(style)
        .filter(([, v]) => v != null && v !== '')
        .map(([k, v]) => {
            const prop = k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
            const val = typeof v === 'number' ? `${v}px` : String(v);
            return `${prop}: ${val} !important;`;
        })
        .join(' ');

// Hover-revealed edit affordance on a preview element; opens that element's style panel
// (mirrors the Search editor's Edit pencil). Position/reveal come from .sf-pencil-host /
// .sf-edit-pencil in StorefrontEditor.css; the parent element needs the sf-pencil-host class.
export const EditPencil: React.FC<{ onClick: () => void; title: string }> = ({ onClick, title }) => (
    <IconButton
        iconProps={{ iconName: 'Edit' }}
        title={title}
        ariaLabel={title}
        className='sf-edit-pencil'
        onClick={(e) => { e.stopPropagation(); onClick(); }}
    />
);

const NO_STYLE_KEY = '__none__';

/**
 * The lightweight chevron accordion used across the style-editing surfaces: a chevron, a small
 * uppercase letter-spaced label, optional smaller trailing text, and an optional info tooltip —
 * a hairline row rather than the boxed chrome of the app-wide <AccordionBox>. Extracted from the
 * Styles panel's part groups (BAND / TITLE / HEADER / …) so the "Advanced overrides" toggles in
 * the section editor and the pencil popovers look and behave identically.
 *
 * Controlled: the Styles panel drives it as a one-open-at-a-time group, while StyleOverridesGroup
 * keeps its own per-mount state. Rendered as a div (not a button) so the tooltip icon can nest.
 */
// Shared geometry for both the collapsible and the static group rows, so a flat surface built from
// a mix of the two lines up exactly.
const styleGroupRow: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '8px 0',
};

/**
 * A NON-collapsible group header in the same visual language as StyleGroupAccordion: hairline top
 * border, optional icon, uppercase bold label, optional info tooltip — but no chevron and nothing
 * to fold. Used where the group's primary control must always be reachable (the section editor's
 * Appearance tab), with only the raw overrides folding underneath via StyleOverridesGroup.
 */
export const StyleGroupHeader: React.FC<{
    label: string;
    iconName?: string;
    tooltip?: string;
}> = ({ label, iconName, tooltip }) => {
    const theme = useTheme();
    return (
        // marginTop opens a paragraph break above the rule so the next group reads as a new block
        // rather than the next line; marginBottom puts air under the label before its first row.
        // (StyleGroupAccordion keeps the tighter rhythm — the Styles panel's part list is a list.)
        <div style={{
            borderTop: `1px solid ${theme.palette.neutralLight}`,
            ...styleGroupRow,
            marginTop: '1.25em',
            marginBottom: '0.5em',
        }}>
            {iconName && <Icon iconName={iconName} style={{ fontSize: 12, color: theme.palette.neutralPrimary }} />}
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.palette.neutralPrimary }}>
                {label}
            </span>
            {tooltip && (
                <TooltipHost content={tooltip}>
                    <Icon iconName='Info' style={{ fontSize: 11, color: theme.palette.neutralTertiary, cursor: 'help' }} />
                </TooltipHost>
            )}
        </div>
    );
};

export const StyleGroupAccordion: React.FC<{
    label: string;
    /** Smaller, non-uppercase trailing text — e.g. "overriding Primary Button". */
    secondaryLabel?: string;
    /** Optional Fluent MDL2 icon between the chevron and the label. Omit for a plain row. */
    iconName?: string;
    tooltip?: string;
    expanded: boolean;
    onToggle: (expanded: boolean) => void;
}> = ({ label, secondaryLabel, iconName, tooltip, expanded, onToggle, children }) => {
    // Lets a settings deep link open this group to reach a control inside it.
    const reveal = useSettingReveal(expanded, () => onToggle(true));
    const theme = useTheme();
    const toggle = () => onToggle(!expanded);
    return (
        <div {...reveal.revealProps} style={{ borderTop: `1px solid ${theme.palette.neutralLight}`, paddingBottom: expanded ? '0.5em' : 0 }}>
            <div
                role='button'
                tabIndex={0}
                onClick={toggle}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
                style={{ ...styleGroupRow, cursor: 'pointer', userSelect: 'none' }}
            >
                <Icon iconName={expanded ? 'ChevronDown' : 'ChevronRight'} style={{ fontSize: 10, color: theme.palette.neutralSecondary }} />
                {iconName && <Icon iconName={iconName} style={{ fontSize: 12, color: theme.palette.neutralPrimary }} />}
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.palette.neutralPrimary }}>
                    {label}
                </span>
                {secondaryLabel && (
                    <span style={{ fontSize: 11, color: theme.palette.neutralTertiary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                        {secondaryLabel}
                    </span>
                )}
                {tooltip && (
                    <TooltipHost content={tooltip}>
                        <Icon iconName='Info' style={{ fontSize: 11, color: theme.palette.neutralTertiary, cursor: 'help' }} onClick={(e) => e.stopPropagation()} />
                    </TooltipHost>
                )}
            </div>
            {expanded && children}
            {/* Collapsed, but a deep link is looking for a setting: render hidden so it can be found
                and this group asked to open. */}
            {reveal.renderCollapsedChildren && <div style={reveal.hiddenStyle}>{children}</div>}
        </div>
    );
};

// A command bar Options-menu toggle carries its explanation as an always-visible second line under
// the name. A native `title` tooltip is the obvious alternative, but inside the menu's Layer it is
// unreliable (and invisible until a long, steady hover), and these page-behavior settings aren't
// guessable from their names alone. The default check mark + item name renders are kept; only the
// content column grows a description line. Pair with OPTION_ITEM_STYLES so the fixed-height row can
// grow. Started life in the Search page editor's Options menu; shared once the Cart page grew one.
export const renderOptionWithDescription = (description: string) =>
    (props: IContextualMenuItemProps, defaultRenders: IContextualMenuItemRenderFunctions): React.ReactNode => (
        <>
            {defaultRenders.renderCheckMarkIcon(props)}
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, padding: '6px 0' }}>
                {defaultRenders.renderItemName(props)}
                <span style={{ fontSize: 11, color: '#605e5c', whiteSpace: 'normal', lineHeight: 1.35, maxWidth: 230, paddingRight: 8 }}>{description}</span>
            </div>
        </>
    );

// The stock menu row is a fixed 36px with a 36px line-height; both have to relax for the
// two-line content above.
export const OPTION_ITEM_STYLES = { root: { height: 'auto', lineHeight: 'normal' }, linkContent: { height: 'auto' } };

/**
 * A multi-part panel's parts, presented as one-open-at-a-time accordions over the flat
 * ElementPropertyRows surface rather than as stacked flat headers: most of these parts have no
 * named-style picker, so their raw rows are always visible, and two adjacent 13-row columns under
 * plain headers read as one continuous list. First part starts open; state lives here, so it resets
 * every time the panel reopens (Fluent unmounts a closed Panel's content).
 *
 * Was private to the Search page editor until the Product page editor grew the same panel shape.
 */
export const StylePartAccordions: React.FC<{
    parts: { key: string; label: string; iconName?: string; tooltip?: string; content: React.ReactNode }[];
    /** Start with every part collapsed, for a panel whose primary content sits above the parts
        (the Search Filters panel's facet toggles, the Description panel's collapsible settings).
        Default: the first part starts open. */
    startCollapsed?: boolean;
    /**
     * Seed the open part, so click-to-edit lands on the element the merchant actually clicked
     * rather than on the panel's first group. A SEED, not a controlled prop: to re-target a panel
     * that is ALREADY open, remount this component (pass the same part as a React `key`).
     */
    initialOpenKey?: string;
}> = ({ parts, startCollapsed, initialOpenKey }) => {
    const [openKey, setOpenKey] = React.useState<string | undefined>(initialOpenKey ?? (startCollapsed ? undefined : parts[0]?.key));
    return (
        <div style={{ marginTop: 12 }}>
            {parts.map((p) => (
                <StyleGroupAccordion
                    key={p.key}
                    label={p.label}
                    iconName={p.iconName}
                    tooltip={p.tooltip}
                    expanded={openKey === p.key}
                    onToggle={(expanded) => setOpenKey(expanded ? p.key : undefined)}
                >
                    {p.content}
                </StyleGroupAccordion>
            ))}
        </div>
    );
};

/**
 * Turn the whole dict the Advanced editor hands back into a PATCH for the surfaces whose updaters
 * merge (`{ navPod: { ...config.navPod, ...patch } }`). A straight spread would keep properties the
 * merchant deleted in the editor, so every current key is first set to undefined; undefined values
 * serialize away, leaving exactly the dict the editor returned.
 */
export const advancedReplacementPatch = (current: { [prop: string]: any } | undefined, css: { [prop: string]: any }): any => {
    const patch: { [prop: string]: any } = {};
    for (const key of Object.keys(current ?? {})) patch[key] = undefined;
    return { ...patch, ...css };
};

/**
 * The "Advanced…" escape at the foot of an open part group: opens the raw key/value CSS editor
 * (StorefrontSectionAdvancedStyleEditorModal) on that group's dict, for the properties the flat rows
 * deliberately don't surface.
 *
 * Centered so it reads as an action rather than another left-aligned group header — left-aligned
 * with an icon it kept being mistaken for one. Extracted from the Styles panel's per-part button so
 * the pod / affiliations style surfaces offer the identical escape in the identical place.
 */
export const AdvancedStyleButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
        <ActionButton
            iconProps={{ iconName: 'Settings' }}
            text='Advanced…'
            onClick={onClick}
            styles={{ root: { height: 28 }, label: { fontSize: 12 } }}
        />
    </div>
);

/**
 * Does a style picker actually render for this element? False when the key maps to no element type,
 * when the user can't administer styles, or when the org has nothing of that type to pick yet.
 *
 * Callers need this because the "Advanced overrides" collapse below is conditional on it: with a
 * picker, the named style is the encouraged path and the raw controls fold away underneath it;
 * without one, folding everything away would leave an empty, hostile surface, so the controls stay
 * exactly as they are today. StyleRefPicker uses the same predicate, so the two can't disagree.
 */
export const useStyleRefPickerVisible = (target?: IStyleTarget, styles?: IStorefrontStyle[]): boolean => {
    const ctx = useStorefrontStyles();
    return !!target && !!ctx.canEdit && stylesForTarget(styles ?? ctx.styles, target).length > 0;
};

// "Style" dropdown — attaches a named Storefront Style to an element by writing the reserved
// `$styleRef` key into that element's style dict (see stylesConfig.ts). The local controls keep
// editing on top of the preset: preset spreads underneath, local keys win. Clearing the dropdown
// removes the ref and leaves the local edits untouched.
//
// `target` says which element type + part this element maps to; pass styleTargetForKey(configKey)
// so the mapping lives in one place rather than being hand-wired per key. Renders nothing when
// the target is unknown, when the user can't administer styles, or when the org has no styles of
// that type yet (there'd be nothing to pick).
export const StyleRefPicker: React.FC<{
    // Deliberately loose: the same picker drives ISearchElementStyle / ISearchCardStyle /
    // ISearchPriceStyle / section StyleProps dicts, which differ in a few property types.
    // All it ever reads or writes is the reserved `$styleRef` key.
    value?: { [prop: string]: any };
    onChange: (patch: { [prop: string]: any }) => void;
    target?: IStyleTarget;
    /** Defaults to the pages-editor context list (server styles + unsaved panel drafts). */
    styles?: IStorefrontStyle[];
    label?: string;
    /**
     * Render as one of the flat rows (label left, underlined dropdown in the fixed column) instead
     * of a stacked labelled Dropdown, so the picker reads as the first setting of its group.
     */
    rowLayout?: boolean;
}> = ({ value, onChange, target, styles, label = 'Style', rowLayout }) => {
    const ctx = useStorefrontStyles();
    const cell = useCellControlStyles();
    const visible = useStyleRefPickerVisible(target, styles);
    const available = stylesForTarget(styles ?? ctx.styles, target);
    if (!visible) return null;

    const ref = getStyleRef(value);
    const selected = findStyle(available, ref);
    const options: IDropdownOption[] = [
        { key: NO_STYLE_KEY, text: 'None' },
        ...available.map((s) => ({ key: s.id, text: isGlobalStyle(s) ? `${s.name} (Global)` : s.name })),
    ];
    // A ref pointing at a deleted style still resolves to local-only; surface it rather than
    // silently showing "None" (the merchant needs to know why the preset stopped applying).
    if (ref && !selected) options.push({ key: ref, text: 'Missing style (deleted)' });

    const onPick = (o?: IDropdownOption) =>
        onChange({ [STYLE_REF_KEY]: o?.key === NO_STYLE_KEY ? undefined : (o?.key as string) });

    if (rowLayout) {
        return (
            <StyleRow label={label}>
                <Dropdown options={options} selectedKey={ref ?? NO_STYLE_KEY} onChange={(_, o) => onPick(o)} styles={cell.dropdown} />
            </StyleRow>
        );
    }
    return (
        <Dropdown
            label={label}
            options={options}
            selectedKey={ref ?? NO_STYLE_KEY}
            onChange={(_, o) => onPick(o)}
        />
    );
};

/**
 * The standard layout wherever a named-style picker and raw per-element controls coexist: the
 * picker on top as the encouraged path, the raw controls folded into a collapsed "Advanced
 * overrides" accordion beneath it.
 *
 *  * Collapsed by DEFAULT even when overrides exist — the nudge toward named styles is the point —
 *    but the toggle row surfaces the state: a count of real local style props, plus the
 *    "overriding <StyleName>" hint that used to sit under the dropdown, so nobody is left
 *    wondering why the element doesn't match the preset.
 *  * Expansion is per-mount session state (a popover re-open or a group re-render starts collapsed);
 *    nothing is persisted.
 *  * When NO picker renders (see useStyleRefPickerVisible), the accordion is skipped entirely and
 *    the controls render exactly as they did before this existed.
 *  * `alwaysVisible` is for functional settings that merely sit in the same group but aren't style
 *    overrides (e.g. the section's edge-to-edge background toggle) — never collapsed.
 *  * `defaultExpanded` seeds that per-mount state open instead of closed. It is a SEED, not a
 *    controlled prop — flipping it later doesn't re-open a group the user has since collapsed. It
 *    exists for deep links: the sections card pencil opens a popover that IS the overrides surface,
 *    so landing there collapsed would show the merchant an empty fold where they expected the
 *    controls they clicked for. Everywhere else the collapsed default (the nudge toward named
 *    styles) stands.
 */
export const StyleOverridesGroup: React.FC<{
    target?: IStyleTarget;
    value?: { [prop: string]: any };
    onChange: (patch: { [prop: string]: any }) => void;
    pickerLabel?: string;
    alwaysVisible?: React.ReactNode;
    /** Render the picker as a flat row (see StyleRefPicker.rowLayout). */
    rowLayout?: boolean;
    /** Start the "Advanced overrides" fold open (initial state only — see the note above). */
    defaultExpanded?: boolean;
}> = ({ target, value, onChange, pickerLabel, alwaysVisible, rowLayout, defaultExpanded, children }) => {
    const ctx = useStorefrontStyles();
    const pickerVisible = useStyleRefPickerVisible(target);
    const [expanded, setExpanded] = React.useState(!!defaultExpanded);

    if (!pickerVisible) {
        return <>{alwaysVisible}{children}</>;
    }

    const overriding = findStyle(ctx.styles, getStyleRef(value))?.name;
    const count = countLocalOverrides(value);
    // "Clear style": un-set every local override but keep the $styleRef, so the element snaps back
    // to the named style's values instead of losing its style assignment along with the tweaks
    // (same semantics as CssCopyPasteButtons' Clear). Emitted as an all-undefined patch — every
    // onChange merges patches over the dict, and undefined values serialize away.
    const clearOverrides = () => {
        if (!value) return;
        const patch: { [prop: string]: undefined } = {};
        for (const key of Object.keys(value)) if (key !== STYLE_REF_KEY) patch[key] = undefined;
        onChange(patch);
    };
    return (
        <>
            <StyleRefPicker value={value} onChange={onChange} target={target} label={pickerLabel} rowLayout={rowLayout} />
            {alwaysVisible}
            {/* Deliberately NOT styled as a group header: left-aligned uppercase rows are reserved
                for StyleGroupHeader / StyleGroupAccordion, and this fold kept being mistaken for one.
                It reads as an action instead — centered, sentence case, chevron-led. The override
                count stays in the text (so a collapsed group still says something is set) and the
                "overriding <StyleName>" hint moves into the tooltip, where it no longer competes for
                room and is anyway echoed by the picker directly above.
                No rule of its own: this is the quiet TAIL of its group, and a hairline here banded it
                against the next group's header rule. Group boundaries are the header's job alone. */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '0.75em', marginBottom: '0.25em' }}>
                <ActionButton
                    iconProps={{ iconName: expanded ? 'ChevronUp' : 'ChevronDown' }}
                    text={count > 0 ? `Advanced overrides (${count})` : 'Advanced overrides'}
                    title={overriding
                        ? `These settings apply on top of the "${overriding}" style. Clear one to fall back to the style's value.`
                        : 'Per-element styling. Picking a style above is usually easier to keep consistent.'}
                    onClick={() => setExpanded(!expanded)}
                    styles={{ root: { height: 28 }, label: { fontSize: 12, fontWeight: 400 } }}
                />
                {/* Quick "back to the style as designed" — only shown when there is something to
                    clear, so the row stays a single quiet action in the common no-overrides case. */}
                {count > 0 && <IconButton
                    iconProps={{ iconName: 'ClearFormatting' }}
                    title={overriding
                        ? `Clear the ${count} override${count === 1 ? '' : 's'} — revert to "${overriding}" as designed`
                        : `Clear the ${count} local setting${count === 1 ? '' : 's'}`}
                    ariaLabel='Clear overrides'
                    onClick={clearOverrides}
                    styles={{ root: { height: 28, width: 28 }, icon: { fontSize: 13 } }}
                />}
            </div>
            {expanded && children}
        </>
    );
};

// The flat-row property surface for ONE element style dict (ISearchElementStyle conventions:
// `font`, numeric percent `fontSize`, numeric px lengths). Deliberately the same set for every part
// of every style — a part is just a CSS dict, and which of these actually matter depends on the
// element, not the part. Ordered as: the general rows first (unlabelled), then TEXT, then BORDER.
//
// Units live in the field as a quiet `suffix` rather than in the row label, so labels stay short and
// the value reads as "12 px". Text transform and letter spacing are deliberately absent — too niche
// for the common surface; they stay fully editable through Advanced… (the part dict passes through
// untouched, so values set there keep working and keep rendering).
//
// Lives HERE rather than in styleRows.tsx (where the chrome it composes lives) purely to avoid an
// import cycle: styleControls already imports styleRows, and the pencil popovers need this surface
// alongside the builders and StyleOverridesGroup from this file. It was the Styles panel's local
// `PartPropertyRows` until the section card popover needed the identical rows.
export const ElementPropertyRows: React.FC<{
    value?: ISearchElementStyle;
    onChange: (patch: Partial<ISearchElementStyle>) => void;
    /**
     * Deep-link id prefix for these rows, e.g. `storefront.affiliations.pod`.
     *
     * These rows render for whatever style dict the caller hands them — a band, a pod, an image, a
     * label — so only the caller knows what the element is. Absent means the rows are simply not
     * linkable, which is the state most of the ~50 call sites are still in.
     */
    settingKeyPrefix?: string;
}> = ({ value, onChange, settingKeyPrefix }) => {
    const cell = useCellControlStyles();
    const mark = (property: string, control: React.ReactElement) =>
        settingKeyPrefix
            ? <DynamicSetting key={property} id={`${settingKeyPrefix}.${property}`}>{control}</DynamicSetting>
            : control;
    return (
        // Every value on this surface lands in an ISearchElementStyle-shaped dict that the builders
        // above run through resolveActiveStyle (and therefore through the token resolver), so the
        // palette/font-pack tokens are safe to offer here.
        <Stack tokens={{ childrenGap: 2 }}>
            {mark('backgroundColor', <ColorRow label='Background' value={value?.backgroundColor} onChange={(v) => onChange({ backgroundColor: v })} allowPaletteTokens />)}
            {mark('padding', (<StyleRow label='Padding'>
                <TextField type='number' min={0} suffix='px' value={value?.padding?.toString() ?? ''} onChange={(_, v) => onChange({ padding: parseNum(v) })} styles={cell.textField} />
            </StyleRow>))}

            <RowGroupLabel text='Text' iconName='Font' />
            {mark('color', <ColorRow label='Text color' value={value?.color} onChange={(v) => onChange({ color: v })} allowPaletteTokens />)}
            {mark('font', (<StyleRow label='Font'>
                <FontPicker selectedFont={value?.font ?? ''} placeholder='Default' onChange={(f) => onChange({ font: f || undefined })} styles={cell.dropdown} allowPaletteTokens />
            </StyleRow>))}
            {mark('fontSize', (<StyleRow label='Font size'>
                <TextField type='number' min={0} suffix='%' value={value?.fontSize?.toString() ?? ''} onChange={(_, v) => onChange({ fontSize: parseNum(v) })} styles={cell.textField} />
            </StyleRow>))}
            {mark('fontWeight', (<StyleRow label='Font weight'>
                <Dropdown options={FONT_WEIGHT_OPTIONS} selectedKey={value?.fontWeight ?? ''} onChange={(_, o) => onChange({ fontWeight: (o?.key as string) || undefined })} styles={cell.dropdown} />
            </StyleRow>))}
            {mark('lineHeight', (<StyleRow label='Line height'>
                <TextField
                    type='number'
                    min={0}
                    step={0.05}
                    placeholder='Auto'
                    title='Space between wrapped lines, as a multiple of the font size (1.5 = one and a half lines). Empty uses the theme default.'
                    value={value?.lineHeight?.toString() ?? ''}
                    onChange={(_, v) => onChange({ lineHeight: parseLineHeight(v) })}
                    styles={cell.textField}
                />
            </StyleRow>))}
            {mark('textAlign', <TextAlignRow value={value?.textAlign as string | undefined} onChange={(v) => onChange({ textAlign: v })} />)}

            <RowGroupLabel text='Border' iconName='BorderDot' />
            {mark('borderColor', <ColorRow label='Border color' value={value?.borderColor} onChange={(v) => onChange({ borderColor: v })} allowPaletteTokens />)}
            {mark('borderWidth', (<StyleRow label='Border width'>
                <TextField type='number' min={0} suffix='px' value={value?.borderWidth?.toString() ?? ''} onChange={(_, v) => onChange({ borderWidth: parseNum(v) })} styles={cell.textField} />
            </StyleRow>))}
            {mark('borderStyle', (<StyleRow label='Border style'>
                <Dropdown options={BORDER_STYLE_OPTIONS} selectedKey={value?.borderStyle ?? ''} onChange={(_, o) => onChange({ borderStyle: (o?.key as string) || undefined })} styles={cell.dropdown} />
            </StyleRow>))}
            {mark('borderRadius', (<StyleRow label='Corner radius'>
                <TextField type='number' min={0} suffix='px' value={value?.borderRadius?.toString() ?? ''} onChange={(_, v) => onChange({ borderRadius: parseNum(v) })} styles={cell.textField} />
            </StyleRow>))}
            {mark('boxShadow', (<StyleRow label='Box shadow'>
                <TextField placeholder='0 2px 8px rgba(0,0,0,.2)' value={value?.boxShadow ?? ''} onChange={(_, v) => onChange({ boxShadow: v || undefined })} styles={cell.textField} />
            </StyleRow>))}
        </Stack>
    );
};

// Shared style controls for a single element (card/box/pod/cell/chip). `includeText`
// adds the text color / font-size / weight controls that make sense for repeated cells,
// chips and pod labels but not for a wrapping container.
//
// `styleTarget` (optional) adds the named-Storefront-Style picker above the controls — pass
// styleTargetForKey(key) from the editor rendering this panel.
export const ElementStyleControls: React.FC<{
    value?: ISearchElementStyle;
    onChange: (patch: Partial<ISearchElementStyle>) => void;
    includeText?: boolean;
    styleTarget?: IStyleTarget;
    /** Suppress the style picker even when a target is known (used inside the Styles panel itself). */
    hideStylePicker?: boolean;
    /**
     * Deep-link id prefix for this element's colour entries, e.g. `storefront.searchCard`.
     *
     * This component renders for whatever style object the caller hands it, so only the caller knows
     * which setting each control actually is. Absent = these controls are not linkable.
     */
    settingKeyPrefix?: string;
}> = ({ value, onChange, includeText, styleTarget, hideStylePicker, settingKeyPrefix }) => {
    const keyFor = (property: string) => (settingKeyPrefix ? `${settingKeyPrefix}.${property}` : undefined);
    // Same shape as ElementPropertyRows' mark: unmarked when no caller supplied a prefix.
    const mark = (property: string, control: React.ReactElement) => {
        const id = keyFor(property);
        return id ? <DynamicSetting key={property} id={id}>{control}</DynamicSetting> : control;
    };
    // ISearchElementStyle dicts, resolved by the builders above - palette tokens are safe here.
    const colorEntries: IStyleEntry[] = [
        { label: 'Background', settingKey: keyFor('backgroundColor'), type: 'color', allowPaletteTokens: true, value: value?.backgroundColor, onChange: (v) => onChange({ backgroundColor: v }), onClear: () => onChange({ backgroundColor: undefined }) },
        { label: 'Border', settingKey: keyFor('borderColor'), type: 'color', allowPaletteTokens: true, value: value?.borderColor, onChange: (v) => onChange({ borderColor: v }), onClear: () => onChange({ borderColor: undefined }) },
    ];
    if (includeText) {
        colorEntries.push({ label: 'Text', settingKey: keyFor('color'), type: 'color', allowPaletteTokens: true, value: value?.color, onChange: (v) => onChange({ color: v }), onClear: () => onChange({ color: undefined }) });
    }
    // The raw per-element controls. When a named-style picker renders for this element they fold
    // into the collapsed "Advanced overrides" group below it; otherwise they render as they always
    // have (StyleOverridesGroup passes them straight through).
    const controls = (
        <Stack tokens={{ childrenGap: 8 }}>
            <StorefrontColorPickerButton label='Colors' entries={colorEntries} style={{ opacity: 1, position: 'static' }} />
            <Stack horizontal wrap tokens={{ childrenGap: 8 }} verticalAlign='end'>
                {mark('borderWidth', <TextField label='Border W' type='number' min={0} value={value?.borderWidth?.toString() ?? ''} onChange={(_, v) => onChange({ borderWidth: parseNum(v) })} styles={numFieldStyles} />)}
                {mark('borderStyle', <Dropdown label='Border' options={BORDER_STYLE_OPTIONS} selectedKey={value?.borderStyle ?? ''} onChange={(_, o) => onChange({ borderStyle: (o?.key as string) || undefined })} styles={{ root: { width: 110 } }} />)}
                {mark('borderRadius', <TextField label='Radius' type='number' min={0} value={value?.borderRadius?.toString() ?? ''} onChange={(_, v) => onChange({ borderRadius: parseNum(v) })} styles={numFieldStyles} />)}
                {mark('padding', <TextField label='Padding' type='number' min={0} value={value?.padding?.toString() ?? ''} onChange={(_, v) => onChange({ padding: parseNum(v) })} styles={numFieldStyles} />)}
            </Stack>
            {includeText && (
                <Stack horizontal wrap tokens={{ childrenGap: 8 }} verticalAlign='end'>
                    {mark('fontSize', <TextField label='Font size %' type='number' min={0} value={value?.fontSize?.toString() ?? ''} onChange={(_, v) => onChange({ fontSize: parseNum(v) })} styles={numFieldStyles} />)}
                    {mark('fontWeight', <Dropdown label='Weight' options={FONT_WEIGHT_OPTIONS} selectedKey={value?.fontWeight ?? ''} onChange={(_, o) => onChange({ fontWeight: (o?.key as string) || undefined })} styles={{ root: { width: 140 } }} />)}
                    {mark('textTransform', <Dropdown label='Transform' options={TEXT_TRANSFORM_OPTIONS} selectedKey={value?.textTransform ?? ''} onChange={(_, o) => onChange({ textTransform: (o?.key as string) || undefined })} styles={{ root: { width: 140 } }} />)}
                    {mark('letterSpacing', <TextField label='Letter-sp' type='number' value={parsePx(value?.letterSpacing)?.toString() ?? ''} onChange={(_, v) => { const n = parseNum(v); onChange({ letterSpacing: n != null ? `${n}px` : undefined }); }} styles={numFieldStyles} />)}
                    {mark('lineHeight', <TextField label='Line height' type='number' min={0} step={0.05} placeholder='Auto' title='Space between wrapped lines, as a multiple of the font size (1.5 = one and a half lines). Empty uses the theme default.' value={value?.lineHeight?.toString() ?? ''} onChange={(_, v) => onChange({ lineHeight: parseLineHeight(v) })} styles={numFieldStyles} />)}
                </Stack>
            )}
            {mark('boxShadow', <TextField label='Box shadow (CSS)' placeholder='e.g. 0 2px 8px rgba(0,0,0,0.2)' value={value?.boxShadow ?? ''} onChange={(_, v) => onChange({ boxShadow: v || undefined })} />)}
        </Stack>
    );

    if (hideStylePicker) return controls;
    return (
        <Stack tokens={{ childrenGap: 8 }}>
            <StyleOverridesGroup target={styleTarget} value={value} onChange={onChange}>
                {controls}
            </StyleOverridesGroup>
        </Stack>
    );
};
